import { strings } from '../strings';
import type { PollutantReading, Station } from '../types/station';

// Pollutants a station can report, in the order they are listed on the station
// card and read out by screen readers.
type PollutantKey = 'pm25' | 'pm10' | 'co' | 'no2' | 'o3' | 'so2';

const pollutantLabels: { key: PollutantKey; label: string }[] = [
  { key: 'pm25', label: strings.pm25 },
  { key: 'pm10', label: strings.pm10 },
  { key: 'co', label: strings.co },
  { key: 'no2', label: strings.no2 },
  { key: 'o3', label: strings.o3 },
  { key: 'so2', label: strings.so2 },
];

/**
 * The pollutants a station reports, each with its label, in a fixed order;
 * pollutants the station does not report are skipped.
 */
export const getStationPollutants = (
  station: Station,
): { key: PollutantKey; label: string; reading: PollutantReading }[] =>
  pollutantLabels.flatMap(({ key, label }) => {
    const reading = station[key];

    return reading ? [{ key, label, reading }] : [];
  });

export const getStationAQI = (station: Station): number | null => {
  const pollutants = [
    station.pm25?.value,
    station.pm10?.value,
    station.co?.value,
    station.no2?.value,
    station.o3?.value,
    station.so2?.value,
  ].filter((value): value is number => value !== undefined);

  if (pollutants.length === 0) {
    return null;
  }

  return Math.max(...pollutants);
};

export const getAQIColor = (aqi: number | null): string => {
  if (aqi === null) {
    return '#b1b4b9';
  }

  if (aqi <= 50) {
    return '#56dc56';
  }

  if (aqi <= 100) {
    return '#ffff51';
  }

  if (aqi <= 150) {
    return '#ff9730';
  }

  if (aqi <= 200) {
    return '#ff4040';
  }

  if (aqi <= 300) {
    return '#b24dbb';
  }

  return '#7e0023';
};

/**
 * Picks a readable text colour (dark or light) for the given `#rrggbb`
 * background, based on its relative luminance, so text drawn on top of an AQI
 * colour stays legible.
 */
export const getContrastTextColor = (hexColor: string): string => {
  const hex = hexColor.replace('#', '');
  const red = parseInt(hex.substring(0, 2), 16) / 255;
  const green = parseInt(hex.substring(2, 4), 16) / 255;
  const blue = parseInt(hex.substring(4, 6), 16) / 255;
  const luminance = 0.2126 * red + 0.7152 * green + 0.0722 * blue;

  return luminance > 0.6 ? '#111827' : '#ffffff';
};

/**
 * Screen-reader label for a station marker: the station name, its city and,
 * when the station reports pollutants, its air quality index.
 */
export const getStationAccessibilityLabel = (station: Station): string => {
  const aqi = getStationAQI(station);
  const parts = [station.name, station.city];

  if (aqi !== null) {
    parts.push(`${strings.airQualityIndex} ${aqi}`);
  }

  return parts.join('، ');
};

/**
 * Screen-reader label for a station list row, where that row is the only thing
 * describing the station: its name and city, the air quality index, every
 * pollutant it reports with how it moved since the previous reading, the
 * distance from the center the list was sorted against, and what a double tap
 * does. The action is part of the label rather than of `accessibilityHint`,
 * because hints are optional and can be switched off.
 */
export const getStationListItemAccessibilityLabel = (
  station: Station,
  distance: string,
): string => {
  const parts = [getStationAccessibilityLabel(station)];
  const pollutants = getStationPollutants(station).map(
    ({ label, reading }) =>
      `${label} ${reading.value} ${strings[reading.change]}`,
  );

  if (pollutants.length > 0) {
    parts.push(`${strings.pollutants} ${pollutants.join('، ')}`);
  }

  parts.push(`${strings.distanceFromMapCenter} ${distance}`);
  parts.push(strings.doubleTapToShowOnMap);

  return parts.join('، ');
};
